// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { AppIcon } from './AppIcon';
import './StatusBanner.css';

const VARIANT_ICONS: Record<string, string> = {
  success: 'check circle',
  error: 'times circle',
  neutral: 'info circle',
};

export interface StatusBannerProps {
  variant: 'success' | 'error' | 'neutral';
  message: ReactNode;
  visible: boolean;
  onDismiss?: () => void;
  dismissible?: boolean;
  autoDismissMs?: number;
  position?: 'fixed' | 'inline';
}

export function StatusBanner({
  variant,
  message,
  visible,
  onDismiss,
  dismissible = true,
  autoDismissMs = 0,
  position = 'fixed',
}: Readonly<StatusBannerProps>) {
  const [show, setShow] = useState(visible);

  const handleDismiss = useCallback(() => {
    setShow(false);
    onDismiss?.();
  }, [onDismiss]);

  useEffect(() => {
    setShow(visible);
  }, [visible]);

  useEffect(() => {
    if (!show || autoDismissMs <= 0) return;

    const timer = setTimeout(handleDismiss, autoDismissMs);
    return () => clearTimeout(timer);
  }, [show, autoDismissMs, handleDismiss]);

  if (!show && position === 'inline') return null;

  const positionClass = position === 'fixed'
    ? `fin-status-banner--fixed${!show ? ' fin-status-banner--hidden' : ''}`
    : 'fin-status-banner--inline';

  return (
    <div
      className={`fin-status-banner fin-status-banner--${variant} ${positionClass}`}
      role="status"
      aria-live="polite"
    >
      <div className="fin-status-banner__content">
        <span className="fin-status-banner__icon">
          <AppIcon name={VARIANT_ICONS[variant]} size={24} />
        </span>
        <span className="fin-status-banner__message">{message}</span>
      </div>
      {dismissible && onDismiss && (
        <button
          className="fin-status-banner__close"
          onClick={handleDismiss}
          type="button"
          aria-label="Dismiss notification"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 4L4 12M4 4L12 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
}
